import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Html5Qrcode } from 'html5-qrcode';
import './URLInput.css';
import CameraUnavailable from '../components/CameraUnavailable';
import { checkCameraPreflight, classifyCameraError, CameraCapability } from '../utils';

const URLInput = () => {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);

  // VISCAT-359: when the camera cannot be used, a non-null reason swaps the
  // scanner for a friendly explanatory message (no manual code-entry fallback).
  const [fallbackReason, setFallbackReason] = useState(null);
  const [isStandalone, setIsStandalone] = useState(false);

  const extractTestUID = (urlString) => {
    try {
      // Extract query string from full URL
      const trimmedUrl = urlString.trim();
      const queryIndex = trimmedUrl.indexOf('?');

      if (queryIndex === -1) {
        return null;
      }

      // Get the query string part 
      const queryString = trimmedUrl.substring(queryIndex);
      const params = new URLSearchParams(queryString);
      return params.get('testUID');
    } catch (err) {
      return null;
    }
  };

  const startScanning = async () => {
    // Prevent creating multiple scanner instances 
    if (html5QrCodeRef.current || isScanning) {
      return;
    }
    try {
      setError('');
      setFallbackReason(null);

      // Synchronous pre-flight: browser floor, secure-context and camera-API
      // checks. Anything but SUPPORTED shows the friendly fallback instead of
      // attempting (and failing) to open the camera.
      const preflight = checkCameraPreflight({ navigator, location: window.location });
      if (preflight.status !== CameraCapability.SUPPORTED) {
        setFallbackReason(preflight.status);
        setIsScanning(false);
        return;
      }

      setIsScanning(true);

      const html5QrCode = new Html5Qrcode("qr-reader");
      html5QrCodeRef.current = html5QrCode;

      // Configuration
      const config = {
        fps: 10,
        qrbox: { width: 300, height: 300 },
        aspectRatio: 1.0
      };

      // Helper function to start scanning with a given config
      const startScanningWithConfig = async (cameraConfig) => {
        return await html5QrCode.start(
          cameraConfig,
          config,
          async (decodedText) => {
            stopScanning();
            const testUID = extractTestUID(decodedText);
            if (testUID) {
              // Hand off to Home, which verifies the testUID and shows the
              // Start button exactly as if the QR link had been opened directly.
              navigate(`/?testUID=${encodeURIComponent(testUID)}`);
            } else {
              setError('QR code does not contain valid testUID');
              setIsScanning(false);
            }
          },
          (errorMessage) => {
            // Ignore scanning errors 
          }
        );
      };

      // Try to get available cameras and prefer rear camera
      let cameraId = null;
      try {
        const devices = await Html5Qrcode.getCameras();
        if (devices && devices.length > 0) {
          // Prefer rear camera (environment), fallback to any available camera
          const rearCamera = devices.find(device =>
            device.label && (
              device.label.toLowerCase().includes('back') ||
              device.label.toLowerCase().includes('rear') ||
              device.label.toLowerCase().includes('environment')
            )
          );

          if (rearCamera) {
            cameraId = rearCamera.id;
          } else {
            // If no rear camera found, use the first available camera
            cameraId = devices[0].id;
          }
        }
      } catch (err) {

      }

      // Try multiple camera configurations
      const configsToTry = [];

      if (cameraId) {
        // First try with specific camera ID (preferred camera)
        configsToTry.push({ deviceId: { exact: cameraId } });
        configsToTry.push({ deviceId: { ideal: cameraId } });
      }

      // Add facingMode options as fallback
      configsToTry.push({ facingMode: "environment" });
      configsToTry.push({ facingMode: "user" });


      for (let i = 0; i < configsToTry.length; i++) {
        try {
          await startScanningWithConfig(configsToTry[i]);
          break;
        } catch (configError) {
          if (i === configsToTry.length - 1) {
            throw configError;
          }
        }
      }

    } catch (err) {
      // Keep the raw detail in the console for support; the candidate only ever
      // sees the friendly, cause-specific message.
      console.error('Camera start failed:', err);
      const { reason, isStandalone: standalone } = classifyCameraError(err, { window, navigator });
      setIsStandalone(standalone);
      setFallbackReason(reason);
      setIsScanning(false);
    }
  };

  const stopScanning = async () => {
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
        await html5QrCodeRef.current.clear();
      } catch (err) {
      }
      html5QrCodeRef.current = null;
    }
    setIsScanning(false);
  };

  useEffect(() => {
    return () => {
      if (html5QrCodeRef.current) {
        stopScanning();
      }
    };
  }, []);

  useEffect(() => {
    // Auto-start scanning when the scan page is opened
    if (!isScanning) {
      startScanning();
    }

  }, []);

  const handleStopScanningClick = async () => {
    await stopScanning();
    navigate('/?skipWelcome=true');
  };

  return (
    <div className="url-input-container">
      <div className="url-input-content">
        {fallbackReason ? (
          <CameraUnavailable reason={fallbackReason} isStandalone={isStandalone} />
        ) : (
          <>
            <h1 className="url-input-title">Scan QR Code</h1>

            <p className="url-input-description">
              Point your camera at the QR code
            </p>
            <div id="qr-reader" ref={scannerRef} className="qr-scanner"></div>
            {error && <p className="url-input-error">{error}</p>}
            <div className="scanner-buttons">
              {isScanning && (
                <button
                  onClick={handleStopScanningClick}
                  className="url-input-button stop-button"
                >
                  Stop Scanning
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default URLInput;


